import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle2, Download, Calendar, Zap, Shield, Users } from 'lucide-react';
import SEOHead from '../seo/SEOHead';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import './Pages.css';

const PricingPage: React.FC = () => {
  const pricingPlans = [
    {
      name: 'Starter',
      price: '8,999',
      period: 'year',
      description: 'Perfect for small gyms and fitness studios',
      features: [
        'Up to 100 Members',
        'Basic Attendance Tracking',
        'Member Management',
        'Manual Check-in',
        'Basic Reports',
        'Email Support',
        '15 Days Free Trial',
        'Data Export to Excel',
        'QR Code Check-in (Coming Soon)'
      ],
      popular: true,
      available: true,
      cta: 'Start Free Trial'
    },
    {
      name: 'Professional',
      price: '***',
      period: 'year',
      description: 'Ideal for growing gyms and multiple locations',
      features: [
        'Up to 500 Members',
        'Advanced Attendance System',
        'Payment Processing',
        'Automated Reminders',
        'Advanced Analytics & Reports',
        'Priority Support',
        'Multi-location Support',
        'Referral Program',
        '15 Days Free Trial',
        'Data Export to Excel',
        'Custom Reports',
        'Bulk Notifications'
      ],
      popular: false,
      available: false,
      cta: 'Contact Sales'
    },
    {
      name: 'Enterprise',
      price: '***',
      period: 'year',
      description: 'For large chains and franchises',
      features: [
        'Unlimited Members',
        'All Professional Features',
        'Progress Tracking System',
        'PT Sessions Management',
        'Class Scheduling & Management',
        'Custom Integrations',
        'Dedicated Account Manager',
        '24/7 Priority Support',
        'White-label Options',
        'API Access',
        '15 Days Free Trial',
        'Data Export to Excel',
        'Custom Training & Onboarding'
      ],
      popular: false,
      available: false,
      cta: 'Contact Sales'
    }
  ];

  return (
    <>
      <SEOHead
        title="Pricing - GymsSync | Affordable Gym Management Software India"
        description="Simple, transparent pricing for GymsSync. Start with 15 days free trial. Yearly plans starting from ₹4,999/year. All your data exportable to Excel."
        keywords="gym software pricing, gym management software cost, fitness studio software pricing India, gymssync pricing, gymsync pricing, gym software price, gym management software price India, gym software cost, gym management software pricing plans, gym software subscription, gym management software rates, gym software pricing comparison, gym management software affordable, gym software free trial, gym management software trial, gym software yearly pricing, gym management software monthly cost"
        ogUrl={typeof window !== 'undefined' ? window.location.href : ''}
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/pricing` : ''}
      />
      <Navbar />
      <div className="page-container">
        <div className="page-header">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="page-title"
          >
            Simple, Transparent Pricing
          </motion.h1>
          <p className="page-subtitle">
            Choose the perfect plan for your gym. All plans include 15 days free trial and data export to Excel.
          </p>
        </div>

        <div className="page-content">
          {/* Trial Banner */}
          <motion.div
            className="trial-banner"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Calendar size={32} />
            <div>
              <h3>15 Days Free Trial</h3>
              <p>Try GymsSync risk-free. No credit card required. Cancel anytime during trial.</p>
            </div>
          </motion.div>

          {/* Pricing Grid */}
          <div className="pricing-grid-page">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                className={`pricing-card-page ${plan.popular ? 'popular' : ''} ${!plan.available ? 'unavailable' : ''}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                whileHover={plan.available ? { y: -10 } : {}}
              >
                {plan.popular && (
                  <div className="popular-badge-page">Most Popular</div>
                )}
                {!plan.available && (
                  <div className="coming-soon-badge-page">Coming Soon</div>
                )}
                <h3>{plan.name}</h3>
                <p className="plan-description">{plan.description}</p>
                <div className="price-section">
                  {plan.price !== '***' && <span className="currency">₹</span>}
                  <span className="price-amount">{plan.price}</span>
                  {plan.price !== '***' && <span className="period">/{plan.period}</span>}
                </div>
                {plan.price !== '***' && (
                  <p className="monthly-equivalent">
                    ≈ ₹{Math.round(parseInt(plan.price.replace(',', '')) / 12).toLocaleString('en-IN')}/month
                  </p>
                )}
                <ul className="features-list-page">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex}>
                      <CheckCircle2 size={18} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {plan.available ? (
                  <a
                    href="https://wa.me/918962327749?text=Hi, I'm interested in GymsSync pricing. Can we schedule a demo meeting?"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pricing-button-page"
                  >
                    {plan.cta}
                  </a>
                ) : (
                  <button
                    className="pricing-button-page disabled"
                    disabled
                  >
                    {plan.cta}
                  </button>
                )}
              </motion.div>
            ))}
          </div>

          {/* Data Export Section */}
          <motion.section
            className="data-export-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="data-export-card">
              <Download size={48} className="export-icon" />
              <h3>Your Data, Your Control</h3>
              <p>
                We believe in complete transparency. At the end of your subscription or anytime you need, 
                we'll export all your gym data to an Excel sheet. Your member information, attendance records, 
                payment history, and reports - everything will be provided to you in a clean, organized format.
              </p>
              <div className="export-features">
                <div className="export-feature">
                  <CheckCircle2 size={20} />
                  <span>Complete member database export</span>
                </div>
                <div className="export-feature">
                  <CheckCircle2 size={20} />
                  <span>Attendance records with dates</span>
                </div>
                <div className="export-feature">
                  <CheckCircle2 size={20} />
                  <span>Payment and transaction history</span>
                </div>
                <div className="export-feature">
                  <CheckCircle2 size={20} />
                  <span>Reports and analytics data</span>
                </div>
              </div>
            </div>
          </motion.section>

          {/* FAQ Section */}
          <motion.section
            className="pricing-faq"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <h2>Frequently Asked Questions</h2>
            <div className="faq-grid">
              <div className="faq-item-page">
                <h4>What happens after the 15-day trial?</h4>
                <p>After your free trial, you can choose to continue with any paid plan. If you decide not to continue, your data will be exported to Excel and provided to you.</p>
              </div>
              <div className="faq-item-page">
                <h4>Can I change plans later?</h4>
                <p>Yes! You can upgrade or downgrade your plan at any time. Changes will be prorated based on your billing cycle.</p>
              </div>
              <div className="faq-item-page">
                <h4>Is there a setup fee?</h4>
                <p>No setup fees. Just choose your plan and start your 15-day free trial. We'll help you get started at no extra cost.</p>
              </div>
              <div className="faq-item-page">
                <h4>What payment methods do you accept?</h4>
                <p>We accept all major payment methods including UPI, credit/debit cards, net banking, and bank transfers.</p>
              </div>
              <div className="faq-item-page">
                <h4>Can I export my data anytime?</h4>
                <p>Yes! You can request a data export at any time. We'll provide all your data in Excel format within 24-48 hours.</p>
              </div>
              <div className="faq-item-page">
                <h4>What if I cancel my subscription?</h4>
                <p>No worries! We'll export all your data to Excel and send it to you. Your data belongs to you, always.</p>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PricingPage;

